import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import { NAV } from "../../content/site";

export const Logo = ({ light }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 md:gap-4 leading-none group">
    <img
      src="/logo-mark.png"
      alt="Home Haven"
      width={554}
      height={433}
      className={`h-9 md:h-11 w-auto transition-all duration-500 ${light ? "brightness-0 invert" : ""}`}
    />
    <span className="flex flex-col">
      <span className={`font-serif text-xl md:text-[24px] tracking-[0.12em] font-semibold transition-colors duration-500 ${light ? "text-white" : "text-ink"}`}>HOME HAVEN</span>
      <span className={`hidden 2xl:block whitespace-nowrap text-[9px] uppercase tracking-[0.26em] mt-1 transition-colors duration-500 ${light ? "text-white/70" : "text-stone"}`}>Real Estate Development & Investment</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  const light = !scrolled && !open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-ink/5 py-3" : "bg-transparent py-5 md:py-7"}`}
    >
      <div className="container-hh flex items-center justify-between">
        <Logo light={light} />
        <div className="flex items-center gap-2">
        <nav className="hidden xl:flex items-center gap-4" aria-label="Menu principale">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[11px] uppercase tracking-[0.14em] font-medium transition-colors duration-300 pb-1 border-b ${n.to === "/" ? "hidden" : n.compactHidden ? "hidden 2xl:block" : ""} ${
                  isActive ? "border-gold" : "border-transparent"
                } ${light ? "text-white/90 hover:text-white" : "text-ink/80 hover:text-ink"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/contatti" data-testid="nav-cta-parliamone" className={`ml-1 btn !py-3 !px-5 whitespace-nowrap ${light ? "bg-white text-ink hover:bg-beige" : "bg-ink text-white hover:bg-ink-soft"}`}>
            Parliamone
          </Link>
          <Link to="/admin" data-testid="nav-admin-link" aria-label="Area riservata" title="Area riservata" className={`p-2 transition-colors ${light ? "text-white/70 hover:text-white" : "text-ink/50 hover:text-ink"}`}>
            <Lock size={16} strokeWidth={1.5} />
          </Link>
        </nav>
        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`2xl:hidden p-2 ${light ? "text-white" : "text-ink"}`}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
        >
          {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
        </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="2xl:hidden fixed inset-0 top-0 bg-cream z-40 pt-28 px-8 pb-24 overflow-y-auto"
          >
            <nav className="flex flex-col gap-6">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i, duration: 0.5 }}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
                    className={({ isActive }) => `font-serif text-4xl font-light tracking-tight ${isActive ? "text-gold" : "text-ink"}`}
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/contatti" data-testid="mobile-nav-cta" className="btn-primary mt-6 self-start">Parliamone</Link>
              <Link to="/admin" data-testid="mobile-nav-admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone mt-4"><Lock size={14} /> Area riservata</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
